import React, { useState, useEffect } from "react";
import {
  getSalespersons,
  addSalesperson,
  updateSalesperson,
  deleteSalesperson,
} from "../../services/apiService";
import "./SalesPerson.css";
import { FaEdit, FaTrash, FaPlus, FaUserTie } from "react-icons/fa";

export default function SalesPersons() {
  const [salespersons, setSalespersons] = useState([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);

  const emptyForm = {
    area_manager_code: "",
    salesperson_name: "",
  };

  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    loadSalespersons();
  }, []);

  const loadSalespersons = async () => {
    try {
      setLoading(true);
      const data = await getSalespersons();
      setSalespersons(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load salespersons:", error);
      alert("Failed to load salespersons.");
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowForm(false);
    setEditId(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!form.area_manager_code.trim() || !form.salesperson_name.trim()) {
      alert("Please fill in all fields.");
      return;
    }

    try {
      if (editId) {
        await updateSalesperson(editId, {
          area_manager_code: form.area_manager_code.trim(),
          salesperson_name: form.salesperson_name.trim(),
        });
      } else {
        await addSalesperson({
          area_manager_code: form.area_manager_code.trim(),
          salesperson_name: form.salesperson_name.trim(),
        });
      }

      closeModal();
      loadSalespersons();
    } catch (error) {
      console.error("Save failed:", error);
      alert("Failed to save salesperson.");
    }
  };

  const handleEdit = (sp) => {
    setForm({
      area_manager_code: sp.area_manager_code || "",
      salesperson_name: sp.salesperson_name || "",
    });
    setEditId(sp.salesperson_id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this salesperson?"
    );
    if (!confirmed) return;

    try {
      await deleteSalesperson(id);
      loadSalespersons();
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete salesperson.");
    }
  };

  const filtered = salespersons.filter((s) => {
    const salespersonName = (s.salesperson_name || "").toLowerCase();
    const areaManagerCode = (s.area_manager_code || "").toLowerCase();
    const keyword = search.toLowerCase();

    return (
      salespersonName.includes(keyword) || areaManagerCode.includes(keyword)
    );
  });

  return (
    <div className="salespersons-container">
      <div className="salespersons-card">
        <div className="salespersons-header">
          <div className="salespersons-title-wrap">
            <div className="salespersons-title-icon">
              <FaUserTie />
            </div>
            <div>
              <h2>Salesperson Master</h2>
              <p>Manage area manager codes and salesperson records.</p>
            </div>
          </div>

          <button
            className="btn-add"
            onClick={() => {
              setEditId(null);
              setForm(emptyForm);
              setShowForm(true);
            }}
          >
            <FaPlus />
            <span>Add Salesperson</span>
          </button>
        </div>

        <div className="salespersons-toolbar">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search salesperson or area manager code..."
              className="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="table-wrapper">
          <table className="salesperson-table">
            <thead>
              <tr>
                <th>Area Manager Code</th>
                <th>Salesperson Name</th>
                <th className="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="3" className="empty-state">
                    Loading salespersons...
                  </td>
                </tr>
              ) : filtered.length > 0 ? (
                filtered.map((sp) => (
                  <tr key={sp.salesperson_id}>
                    <td>{sp.area_manager_code}</td>
                    <td>{sp.salesperson_name}</td>
                    <td>
                      <div className="action-group">
                        <button
                          className="icon-btn edit"
                          onClick={() => handleEdit(sp)}
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="icon-btn delete"
                          onClick={() => handleDelete(sp.salesperson_id)}
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="empty-state">
                    No salesperson found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={closeModal}>
          <div
            className="modal-content form-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>{editId ? "Edit Salesperson" : "Add Salesperson"}</h3>
              <p>
                {editId
                  ? "Update salesperson information below."
                  : "Enter salesperson details below."}
              </p>
            </div>

            <div className="form-grid">
              <div className="form-row">
                <label>Area Manager Code</label>
                <input
                  type="text"
                  value={form.area_manager_code}
                  onChange={(e) =>
                    setForm({ ...form, area_manager_code: e.target.value })
                  }
                  placeholder="Enter area manager code"
                />
              </div>

              <div className="form-row">
                <label>Salesperson Name</label>
                <input
                  type="text"
                  value={form.salesperson_name}
                  onChange={(e) =>
                    setForm({ ...form, salesperson_name: e.target.value })
                  }
                  placeholder="Enter salesperson name"
                />
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-save" onClick={handleSave}>
                {editId ? "Update" : "Save"}
              </button>
              <button className="btn-cancel" onClick={closeModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}